import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import classNames from 'classnames';
import useDimensions from 'react-cool-dimensions';
import Select from './Select';
import { hasValue } from '../../../../utils';
import { UXPContext } from '@core/context/Context';
import { ResizeObserver } from "@juggle/resize-observer";
import { getInputSizeStyles, getInputStateClass } from './utils';
/**
 * This component presents a group of options from which one can be selected.
 * Suitable to select a single item from a list where the list is very small. Most often used for filters.
 * Follows v5 input pattern with theme support and keyboard navigation
 * @export
 */
const ToggleFilter = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const context = React.useContext(UXPContext);
    let { options, value, onChange, className, minWidth, maxWidth, width: widthProp, fullWidth, disabled, readOnly, isValid } = props;
    const [width, setWidth] = React.useState(10000);
    const [focusedIndex, setFocusedIndex] = React.useState(-1);
    const containerRef = React.useRef(null);
    const optionRefs = React.useRef([]);
    const showDropdown = (!!(props === null || props === void 0 ? void 0 : props.renderAsDropdown) && hasValue((_a = props === null || props === void 0 ? void 0 : props.renderAsDropdown) === null || _a === void 0 ? void 0 : _a.minWidth) && width < (props.renderAsDropdown.minWidth));
    const renderAsPill = showDropdown && (hasValue((_c = (_b = props === null || props === void 0 ? void 0 : props.renderAsDropdown) === null || _b === void 0 ? void 0 : _b.renderAsPill) === null || _c === void 0 ? void 0 : _c.minWidth) || hasValue((_e = (_d = props === null || props === void 0 ? void 0 : props.renderAsDropdown) === null || _d === void 0 ? void 0 : _d.renderAsPill) === null || _e === void 0 ? void 0 : _e.maxWidth));
    const { observe } = useDimensions({
        useBorderBoxSize: true,
        polyfill: ResizeObserver,
        onResize: ({ width: w, height: h }) => {
            var _a;
            if (hasValue((_a = props === null || props === void 0 ? void 0 : props.renderAsDropdown) === null || _a === void 0 ? void 0 : _a.minWidth)) {
                setWidth(w);
            }
        }
    });
    const handleClick = (newValue) => {
        if (disabled || readOnly)
            return;
        onChange(newValue);
    };
    const handleKeyDown = (e, optionValue, index) => {
        var _a, _b;
        if (disabled || readOnly)
            return;
        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                handleClick(optionValue);
                break;
            case 'ArrowLeft':
                e.preventDefault();
                const prevIndex = index > 0 ? index - 1 : options.length - 1;
                (_a = optionRefs.current[prevIndex]) === null || _a === void 0 ? void 0 : _a.focus();
                setFocusedIndex(prevIndex);
                break;
            case 'ArrowRight':
                e.preventDefault();
                const nextIndex = index < options.length - 1 ? index + 1 : 0;
                (_b = optionRefs.current[nextIndex]) === null || _b === void 0 ? void 0 : _b.focus();
                setFocusedIndex(nextIndex);
                break;
        }
    };
    // render option
    const renderOption = (option, index) => {
        const isSelected = value == option.value;
        return (_jsx("div", { ref: el => optionRefs.current[index] = el, className: classNames("uxpcore_togglefilter__option", {
                "uxpcore_togglefilter__option--selected": isSelected,
                "uxpcore_togglefilter__option--disabled": disabled,
                "uxpcore_togglefilter__option--readonly": readOnly
            }), onClick: () => handleClick(option.value), onKeyDown: (e) => handleKeyDown(e, option.value, index), tabIndex: disabled || readOnly ? -1 : 0, role: "radio", "aria-checked": isSelected, "aria-disabled": disabled, "aria-readonly": readOnly, children: option.label }, index));
    };
    const dropdownProps = {};
    if (renderAsPill) {
        dropdownProps.renderPlaceholder = {
            renderAsPill: {
                minWidth: (_g = (_f = props === null || props === void 0 ? void 0 : props.renderAsDropdown) === null || _f === void 0 ? void 0 : _f.renderAsPill) === null || _g === void 0 ? void 0 : _g.minWidth,
                maxWidth: (_j = (_h = props === null || props === void 0 ? void 0 : props.renderAsDropdown) === null || _h === void 0 ? void 0 : _h.renderAsPill) === null || _j === void 0 ? void 0 : _j.maxWidth
            }
        };
    }
    const containerStyles = getInputSizeStyles({ minWidth, maxWidth, width: widthProp, fullWidth, style: undefined });
    const stateClass = getInputStateClass({ disabled, readOnly, isValid });
    // When in dropdown mode, render Select directly without ToggleFilter styling
    if (showDropdown) {
        return (_jsx("div", { ref: observe, children: _jsx(Select, Object.assign({ selected: value, onChange: (v) => onChange(v), options: options }, dropdownProps, { className: className, disabled: disabled, readOnly: readOnly, minWidth: minWidth, maxWidth: maxWidth, width: widthProp, fullWidth: fullWidth, isValid: isValid })) }));
    }
    // Pill mode - render with ToggleFilter styling
    return (_jsx("div", { ref: observe, className: classNames("uxpcore_input", // Base class for sizing (min-width: 8rem, max-width: 33rem)
        "uxpcore_togglefilter", className, stateClass), style: containerStyles, role: "radiogroup", children: options.map((option, index) => renderOption(option, index)) }));
};
export default ToggleFilter;
